import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../services/api';
import { useUser } from '../contexts/UserContext';
import type { PenkiClaimStatus } from '../types';
import { formatCountdown, formatPenki } from '../utils/penki';
import './PenkiClaimCard.css';

export const PenkiClaimCard: React.FC = () => {
  const { refreshUser } = useUser();
  const [status, setStatus] = useState<PenkiClaimStatus | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [pending, setPending] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await apiClient.getPenkiClaimStatus();
      setStatus(next);
      setSecondsLeft(next.remaining_seconds);
    } catch (err) {
      console.error('Failed to load penki claim status:', err);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!status || secondsLeft <= 0) return;

    const id = window.setInterval(() => {
      setSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => window.clearInterval(id);
  }, [status, secondsLeft > 0]);

  useEffect(() => {
    if (secondsLeft !== 0) return;
    if (!status || status.remaining_seconds === 0) return;
    void load();
  }, [secondsLeft, status, load]);

  const handleClaim = async () => {
    if (pending || !status?.can_claim) return;
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
    }
    setPending(true);
    try {
      const result = await apiClient.claimPenki();
      setStatus({
        available_penki: status.available_penki,
        can_claim: false,
        next_claim_at: result.next_claim_at,
        remaining_seconds: result.remaining_seconds,
      });
      setSecondsLeft(result.remaining_seconds);
      await refreshUser();
    } catch (err: unknown) {
      const remaining = (err as { response?: { data?: { remaining_seconds?: number } } })
        ?.response?.data?.remaining_seconds;
      if (typeof remaining === 'number') {
        setSecondsLeft(remaining);
      }
      await load();
    } finally {
      setPending(false);
    }
  };

  if (!status) return null;

  if (secondsLeft > 0) {
    return (
      <section className="daily-penki-claimed" aria-live="polite">
        <div className="daily-penki-text">
          <span className="daily-penki-title">✅ Забрал(а) УЙМЫ</span>
          <span className="daily-penki-subtitle">Следующие уже на подходе</span>
        </div>
        <div className="daily-penki-timer">{formatCountdown(secondsLeft)}</div>
      </section>
    );
  }

  return (
    <section className="daily-penki-banner">
      <div className="daily-penki-text">
        <span className="daily-penki-title">🎁 +{formatPenki(status.available_penki)} УЙМ упали!</span>
        <span className="daily-penki-subtitle">Ежедневные УЙМЫ — хватай пока горячие</span>
      </div>
      <button
        type="button"
        className="daily-penki-claim-btn"
        onClick={handleClaim}
        disabled={pending || !status.can_claim}
      >
        Забрать
      </button>
    </section>
  );
};
